
import { Team } from "@/types"
import { Component, Vue, Prop } from "vue-property-decorator"

import DataTable from "../DataTable.vue"

import { Folder, FolderType } from "@/types/folder"
import api from "@/services/api"

import StringValidation from "@/scripts/stringValidation"
import { folderMixin } from "@/mixins/folderMixin"
import { Getter } from "vuex-class"
import { CurrentUser } from "@/types/user"
import { TenantFeatures } from "@/types/tenantFeatures"

@Component({
	components: {
		"data-table": DataTable
	}
})
export default class FolderSetup extends Vue {
	@Prop({ default: () => [] })
	value!: Folder[]

	@Prop({ default: FolderType.Insight })
	folderType!: FolderType

	@Prop({ default: () => [] })
	teams!: Team[];

	@Prop({ default: false })
	hide!: boolean;

	@Getter("getMe") public me!: CurrentUser;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	tenantFeatures = TenantFeatures

	private currentSubFolderList: Folder[] = []
	private validParentFolders: Folder[] = this.value

	get folderColumns () : any[] {
		return [
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => {
					// Validate that the title doesn't contain backslash character
					if (item.title.includes("\\")) {
						return "Folder name contains illegal character \\"
					} else if (item.parentFolderId && item.parentFolderId !== null) {
						// Validate that the sub folder does not have the same title as any other sub folders within the same folder structure
						if (this.value.some(folder =>
							folder.parentFolderId === item.parentFolderId &&
							folder.title === item.title && folder.id !== item.id)) {
							return "Folder name already exists within selected parent folder"
						}
					} else if (this.value.some(folder => folder.parentFolderId === null && folder.title === item.title && folder.id !== item.id)) {
						// Validate that the root folder does not have the same title as any other root folders
						return "Folder name already in use"
					}

					return StringValidation.validateTitle(item.title)
				}
			},
			{
				key: "parentFolder",
				label: "Parent Folder",
				dataType: "list",
				inTable: false,
				inModal: () => this.hasSubFolderAccess, // Only displayed if user has sub folder access
				valueColumn: "parentFolderId",
				options: this.validParentFolders,
				additionalMessage: (item: any) => {
					if (!this.areAllParentFoldersValid(item.id)) {
						return "Existing sub folders for the selected folder are not available for selection"
					}
				}
			},
			{
				key: "pathToRoot",
				label: "Current Folder Path",
				sortable: true,
				readonly: true,
				inTable: this.hasSubFolderAccess, // Only displayed if user has sub folder access
				inModal: (item: any) => this.hasSubFolderAccess && item.pathToRoot
			},
			{
				key: "teams",
				hasTemplate: true,
				label: "Roles",
				dataType: "list-multiple",
				valueColumn: "teams",
				options: this.teams
			}
		]
	}

	get hasSubFolderAccess () {
		// Allow access if feature is enabled or there are existing sub folders
		return ((this.me && this.me.tenantEnabledFeatures!.includes(TenantFeatures.SubFolders)) || this.value.some(f => f.parentFolderId && f.parentFolderId !== null))
	}

	private beforeUpdate () {
		// Ensure the folder paths are up to date on component re-render
		this.buildFolderPaths()
	}

	private mounted () {
		// Ensure the folder paths are up to date before component renders
		this.buildFolderPaths()
	}

	// Get the list of folder paths for all folders
	private getFolderPaths (folderList: Folder[]): string[] {
		let folderPathList: string[] = []

		folderList.forEach(folder => {
			folderPathList.push(folderMixin.methods.getPathToRootFolder(folder, folderList, true))
		})

		return folderPathList
	}

	private buildSubFolderList (folderId: string) {
		// Create list of direct sub folders of provided folder ID
		let subFolderList: Folder[] = this.value.filter(f => f.parentFolderId === folderId)

		// Loop through each sub folder, recursively getting any direct sub folders
		for (let i = 0, count = subFolderList.length; i < count; i++) {
			// Add sub folder to the list
			this.currentSubFolderList.push(subFolderList[i])
			// Get direct sub folders of current sub folder
			this.buildSubFolderList(subFolderList[i].id)
		}
	}

	private areAllParentFoldersValid (selectedFolderId: string) {
		if (selectedFolderId) {
			// Minus 1 from the complete list of folders as the selected folder should not be considered
			return this.validParentFolders.length === (this.value.length - 1)
		} else {
			return this.validParentFolders.length === this.value.length
		}
	}

	private get teamsById (): { [key: string]: Team } {
		return this.teams.toDictionary("id")
	}

	private async saveFolder (folder: Folder) {
		// force loading state during folder structure update
		this.$emit("setLoadingState", true)
		const updatedFolder = await api.folderSet(folder)

		if (folder.id) {
			const folderIndex = this.value.findIndex(f => f.id === folder.id)
			if (folderIndex === -1) {
				// In case we can't find the folder we're updating in the list, just refresh the whole list
				this.$emit("refreshFolders")
			} else {
				this.value.splice(folderIndex, 1, updatedFolder)
			}
		} else {
			const existingIndex = this.value.findIndex(f => f.title === folder.title)
			if (existingIndex > -1) {
				this.value.splice(existingIndex, 1, updatedFolder)
			} else {
				this.value.push(updatedFolder)
			}
		}

		// remove loading state - refreshFolders handler can optionally set another loading state
		this.$emit("setLoadingState", false)

		this.value = this.value.sortBy("title")
		this.$emit("input", this.value)

		this.$emit("refreshFolders")
	}

	private createFolder () {
		// Reset the list of valid parent folders to all as this is a new folder
		this.validParentFolders = this.value

		return { id: null, title: "", teams: [], folderType: this.folderType }
	}

	private async deleteFolder (folder: Folder) {
		if (!folder.id) return

		await api.folderDelete(folder)
		this.value.removeWhere(f => f.id === folder.id)
		this.$emit("input", this.value)
		this.$emit("folderDeleted", folder)
	}

	private async folderUsage (folder: Folder): Promise<{ id: string, label: string }[]> {
		return api.folderUsage(folder)
	}

	private buildFolderPaths () {
		// Calculate the path to root folder for all folders
		this.value.forEach(folder => {
			folder.pathToRoot = folderMixin.methods.getPathToRootFolder(folder, this.value)
		})
	}

	private getValidParentFolders (folder: Folder) {
		this.currentSubFolderList = []
		// Existing folder, check sub folders
		if (folder.id !== null) {
			this.buildSubFolderList(folder.id)

			// Exclude non valid folders from the list of potential parent folders
			this.validParentFolders = this.value.filter(f => !this.currentSubFolderList.includes(f) && folder.id !== f.id)
		}
	}

	public canDeleteFolder (folder: Folder): boolean {
		// Check if the folder has any sub folders, if so then the folder can't be deleted
		this.getValidParentFolders(folder)
		if (this.currentSubFolderList && this.currentSubFolderList.length > 0) {
			return false
		} else {
			return true
		}
	}

	private get folderTypeName (): string {
		// Get folder type as string with spaces in between words
		let name = FolderType[this.folderType].replace(/([A-Z])/g, " $1").trim()
		return `${name}s`
	}
}

